/**
 * Senses — how an animal finds out you are there (M2-05).
 *
 * The design rule the whole wildlife ticket rests on: **behaviour is driven by
 * perception, not by an aggro radius**. A wolf that knew where you were because
 * you were within twelve tiles of it is a wolf you cannot stalk, cannot flank
 * and cannot avoid, and the Great Hunt (M5) is only as good as that distinction.
 *
 * So there are two channels, and they have different shapes on purpose:
 *
 * - **Sight** is a cone. It is long and it is directional, so an animal has a
 *   blind side and turning its head is a thing that costs it time. Walls stop
 *   it, because it is light.
 * - **Smell** is a circle carried on the wind. It is shorter, it does not care
 *   which way anybody is facing, and it does not care about walls — which is
 *   exactly why approaching downwind is the thing a hunter learns. Upwind of a
 *   stag you are announced at twice the distance you would be seen at.
 *
 * Everything here is pure and integer-compared. A perception check that came out
 * of a float is a perception check that can disagree between two machines on the
 * tile where it rounds, and "did the stag see me" decides whole hunts.
 *
 * Wind arrives from world state since M3-05 — `weather.wind`, which veers over
 * the hours. `WORLD_WIND` remains the default for a caller with no world in
 * hand (the offline harness, and the tests that predate the sky).
 */
import { chebyshev, DIRECTION_VECTORS, DIRECTIONS } from './actor.js';
import { deriveCapabilities } from './body.js';
import { isPassable } from './map.js';
/* -------------------------------------------------------------------------- *
 * Wind (M3-05's seam)
 * -------------------------------------------------------------------------- */
/**
 * Which way the wind blows *from* when nobody says otherwise.
 *
 * It was a constant until M3-05; it is now the fallback, and the live value is
 * `world.weather.wind`. Cwm Twrch runs south-west to north-east and the
 * prevailing weather comes up it off the sea, so this is still where the wind
 * spends most of its time.
 */
export const WORLD_WIND = 'sw';
/**
 * Is `from` upwind of `at` — i.e. does the wind carry their scent to it?
 *
 * Upwind means standing on the side the wind comes *from*, so your scent is
 * being delivered. It is the mistake a hunter makes once.
 */
export function isUpwind(at, from, wind = WORLD_WIND) {
    const [wx, wy] = DIRECTION_VECTORS[wind];
    // Wind vector points *from* the quarter it blows out of, so a scent source
    // on that side has a positive projection onto it.
    return (from.x - at.x) * wx + (from.y - at.y) * wy > 0;
}
export const DEFAULT_SENSES = { sight: 8, arc: 3, smell: 4, upwindFactor: 2 };
/**
 * How much of an animal's eyesight a sneaking hunter takes away (M5-03).
 *
 * Half the sight range, and **none of the smell**. That asymmetry is the whole
 * of the mechanic and it is deliberate: creeping is how you beat a stag's eyes,
 * and there is nothing you can do about its nose except be downwind of it. A
 * hunter who sneaks upwind still gets caught, which is the lesson M2-05 exists
 * to teach and the one this ticket must not quietly repeal.
 *
 * Applied to the watcher's range rather than modelled as a noise value, because
 * the ticket asks for a modifier set and not a stealth meter — and because "the
 * stag sees you from seven tiles instead of fourteen" is a sentence a player can
 * act on.
 */
export const SNEAK_SIGHT_FACTOR = 0.5;
/**
 * Is `target` inside the cone `watcher` is facing?
 *
 * Compared in compass steps rather than in angles, for the determinism reason
 * at the top of the file — and because eight directions is the resolution the
 * rest of the sim already thinks in.
 */
export function withinArc(watcher, target, arc) {
    if (arc >= 4)
        return true;
    const dx = target.x - watcher.x;
    const dy = target.y - watcher.y;
    if (dx === 0 && dy === 0)
        return true;
    const toward = bearingOf(dx, dy);
    const a = DIRECTIONS.indexOf(watcher.facing);
    const b = DIRECTIONS.indexOf(toward);
    const delta = Math.min(Math.abs(a - b), DIRECTIONS.length - Math.abs(a - b));
    return delta <= arc;
}
/** The eight-way bearing of an offset. Shares `combat.ts`'s snapping rule. */
function bearingOf(dx, dy) {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const ns = dy < 0 ? 'n' : dy > 0 ? 's' : '';
    const ew = dx > 0 ? 'e' : dx < 0 ? 'w' : '';
    if (ns && ew) {
        if (ax * 2 < ay)
            return ns;
        if (ay * 2 < ax)
            return ew;
        return `${ns}${ew}`;
    }
    return (ns || ew);
}
/**
 * Can light get from one tile to the other?
 *
 * A supercover walk rather than a plain Bresenham: it visits every tile the
 * line touches, including the two it clips at a diagonal, so you cannot see
 * through the corner where two walls meet. A hunter who could would be a hunter
 * shooting through a hut.
 */
export function hasLineOfSight(map, fromX, fromY, toX, toY) {
    let x = fromX;
    let y = fromY;
    const dx = Math.abs(toX - fromX);
    const dy = Math.abs(toY - fromY);
    const sx = fromX < toX ? 1 : -1;
    const sy = fromY < toY ? 1 : -1;
    let error = dx - dy;
    // Bounded by the map's own diagonal: a malformed call cannot spin forever.
    for (let step = 0; step < dx + dy + 2; step += 1) {
        if (x === toX && y === toY)
            return true;
        // The endpoints themselves are never the obstruction — you can see a man
        // standing in a doorway, and you can see out of one.
        if (!(x === fromX && y === fromY) && !isPassable(map, x, y))
            return false;
        const doubled = error * 2;
        if (doubled > -dy) {
            error -= dy;
            x += sx;
        }
        else if (doubled < dx) {
            error += dx;
            y += sy;
        }
    }
    return false;
}
/**
 * What this species' senses are worth to *this* body — a half-blinded animal
 * sees half as far.
 *
 * Smell is left alone by `sight` damage deliberately: an animal with a ruined
 * eye still has its nose, and that asymmetry is what makes blinding a wolf
 * change the fight rather than end it.
 */
export function sensesOf(senses, plan, 
/** Anything with a body. Structural, so this module stays clear of the kit. */
bearer) {
    if (!plan || !bearer)
        return senses;
    const sight = deriveCapabilities(plan, bearer.vitals.body).sight;
    return { ...senses, sight: Math.round(senses.sight * sight) };
}
/**
 * Everything `watcher` can currently perceive, nearest first.
 *
 * Sight is checked before smell so that a thing both seen and smelled reports
 * as seen — which is the more actionable fact, and the one the log should say.
 */
export function perceive(world, watcher, senses, wind = WORLD_WIND) {
    const found = [];
    for (const target of Object.values(world.actors)) {
        if (target.id === watcher.id)
            continue;
        const distance = chebyshev(watcher.x, watcher.y, target.x, target.y);
        // Creeping shortens the eye and leaves the nose alone. See
        // `SNEAK_SIGHT_FACTOR` for why that asymmetry is the point.
        const sight = world.combat?.[target.id]?.sneaking === true
            ? Math.floor(senses.sight * SNEAK_SIGHT_FACTOR)
            : senses.sight;
        if (distance <= sight &&
            withinArc(watcher, target, senses.arc) &&
            hasLineOfSight(world.map, watcher.x, watcher.y, target.x, target.y)) {
            found.push({ actorId: target.id, sense: 'sight', distance });
            continue;
        }
        // Scent does not care which way anybody is facing, and does not care about
        // walls. It cares entirely about which side of you the wind is coming from.
        const reach = isUpwind(watcher, target, wind)
            ? Math.round(senses.smell * senses.upwindFactor)
            : senses.smell;
        if (distance <= reach) {
            found.push({ actorId: target.id, sense: 'smell', distance });
        }
    }
    // Sorted so the choice of quarry is a fact about the world rather than about
    // the insertion order of the actor table.
    return found.sort((a, b) => a.distance - b.distance || a.actorId.localeCompare(b.actorId));
}
//# sourceMappingURL=senses.js.map